import { supabase } from './client';
import { createClient } from '@supabase/supabase-js';
import { v4 as uuidv4 } from 'uuid';

// Supabase Storage Configuration for private buckets
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "https://sjyldlubmtfoujonmtru.supabase.co";
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Bucket configuration
const BUCKET_NAMES = {
  'project-images': import.meta.env.VITE_PROJECT_IMAGES_BUCKET || 'private-project-images',
  'profile-avatars': import.meta.env.VITE_PROFILE_AVATARS_BUCKET || 'private-profile-avatars',
  'site-photos': import.meta.env.VITE_SITE_PHOTOS_BUCKET || 'private-site-photos',
  'documents': import.meta.env.VITE_DOCUMENTS_BUCKET || 'private-documents'
} as const;

// Debug environment variables on startup


/**
 * Validates user authentication for storage operations
 */
async function validateUserAuth() {
  try {
    
    const { data: { user }, error } = await supabase.auth.getUser();
    
    if (error) {
      
      throw new Error(`Authentication error: ${error.message}`);
    }
    
    if (!user) {
      
      throw new Error('User must be authenticated to access storage');
    }
    
    
    
    return user;
  } catch (error) {
    
    throw error;
  }
}

/**
 * Generate a user-specific key for file storage
 * Format: userId/path/filename (without duplicate bucket name)
 */
function generateUserSpecificKey(
  userId: string,
  bucket: keyof typeof BUCKET_NAMES,
  fileName: string,
  path?: string
): string {
  const fileExt = fileName.split('.').pop();
  const uniqueFileName = `${uuidv4()}.${fileExt}`;
  
  // Simple path structure: userId/optional-path/filename
  // Don't include bucket name in the path since it's already specified in .from(bucket)
  if (path) {
    return `${userId}/${path}/${uniqueFileName}`;
  }
  return `${userId}/${uniqueFileName}`;
}

/**
 * Create a simple Supabase client for storage operations only
 * This bypasses any custom fetch configurations that might be causing issues
 */
function createSimpleStorageClient() {
  
  
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    throw new Error('Missing Supabase environment variables for storage client');
  }
  
  return createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: {
      autoRefreshToken: false,
      persistSession: false,
      detectSessionInUrl: false
    },
    global: {
      headers: {
        'X-Client-Info': 'santiyex-storage-upload'
      }
    }
  });
}

/**
 * Upload a file to Supabase Storage with user-specific access
 */
export async function uploadFileToSupabaseS3(
  file: File,
  bucket: keyof typeof BUCKET_NAMES,
  path?: string
): Promise<string> {
  try {
    
    

    // Debug environment variables and client configuration
    

    // Validate user authentication
    
    const user = await validateUserAuth();
    

    // Validate file
    if (!file.size) {
      
      throw new Error('Invalid file: file has zero size');
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB max
      
      throw new Error('File size exceeds the 10MB limit');
    }

    

    // Generate user-specific key
    const key = generateUserSpecificKey(user.id, bucket, file.name, path);
    const bucketName = BUCKET_NAMES[bucket];

    

    // Check if bucket exists/is accessible (Note: listBuckets requires admin privileges)
    
    try {
      const { data: bucketData, error: bucketError } = await supabase.storage.listBuckets();
      
      if (bucketError) {
        
      }
    } catch (bucketTestError) {
      
    }

    // Test bucket access by trying to list files in the specific bucket
    
    try {
      const { data: testData, error: testError } = await supabase.storage
        .from(bucketName)
        .list('', { limit: 1 });
      
      if (testError) {
        
      } else {
        
      }
    } catch (bucketAccessError) {
      
    }

    // Test file readability and format
    
    try {
      // Check if file is readable as ArrayBuffer
      const arrayBuffer = await file.arrayBuffer();
      
      
      // Create a new File from the ArrayBuffer to ensure compatibility
      const processedFile = new File([arrayBuffer], file.name, { type: file.type });
      
      
      // Use the processed file for upload
      file = processedFile;
    } catch (fileError) {
      
      throw new Error(`File processing failed: ${fileError.message}`);
    }

    // Test basic connectivity to Supabase
    
    try {
      const connectivityStartTime = Date.now();
      const { data: healthData, error: healthError } = await supabase.from('projects').select('count').limit(1);
      const connectivityEndTime = Date.now();
      
      if (healthError) {
        
      } else {
        
      }
    } catch (connectivityError) {
      
      throw new Error(`Network connectivity issue: ${connectivityError.message}`);
    }

    // Upload file using Supabase storage
    
    
    
    const uploadStartTime = Date.now();
    
    let data, error;
    try {
      
      
      // Test if storage client is properly initialized
      const storageClient = supabase.storage.from(bucketName);
      
      
      const result = await storageClient.upload(key, file, {
        cacheControl: '3600',
        upsert: false,
      });
      
      data = result.data;
      error = result.error;
      
      
      
    } catch (uploadException) {
      
      
      // Check if this is a network error
      if (uploadException?.message?.includes('Load failed') || 
          uploadException?.message?.includes('NetworkError') ||
          uploadException?.name === 'TypeError') {
        
        
        
        
        
        
        
        // Try with a fresh, simple client
        
        try {
          const simpleClient = createSimpleStorageClient();
          
          // Copy the session from the main client
          const { data: sessionData } = await supabase.auth.getSession();
          if (sessionData.session) {
            await simpleClient.auth.setSession({
              access_token: sessionData.session.access_token,
              refresh_token: sessionData.session.refresh_token
            });
            
          }
          
          const simpleResult = await simpleClient.storage
            .from(bucketName)
            .upload(key, file, {
              cacheControl: '3600',
              upsert: false,
            });
          
          data = simpleResult.data;
          error = simpleResult.error;
          
          
        } catch (simpleClientError) {
          
          throw uploadException; // Throw the original error
        }
      } else {
        throw uploadException;
      }
    }

    const uploadEndTime = Date.now();
    

    if (error) {
      
      throw error;
    }

    

    if (!data || !data.path) {
      
      throw new Error('Upload succeeded but no file path was returned');
    }

    // For private buckets, we'll return a special format that includes both bucket and path
    // This will allow the frontend to generate signed URLs on-demand
    
    
    const fileReference = `supabase-storage://${bucketName}/${data.path}`;
    
    
    
    return fileReference;

  } catch (error) {
    
    throw error;
  }
}

/**
 * Generate a signed URL for secure file download
 */
export async function generateSignedDownloadUrl(
  bucket: keyof typeof BUCKET_NAMES,
  key: string,
  expiresInSeconds: number = 3600
): Promise<string> {
  try {
    // Validate user authentication
    await validateUserAuth();
    
    const bucketName = BUCKET_NAMES[bucket];

    const { data, error } = await supabase.storage
      .from(bucketName)
      .createSignedUrl(key, expiresInSeconds);

    if (error) {
      
      throw error;
    }

    if (!data || !data.signedUrl) {
      throw new Error('Failed to generate signed URL');
    }

    return data.signedUrl;
  } catch (error) {
    
    throw error;
  }
}

/**
 * Delete a file from Supabase Storage
 */
export async function deleteFileFromSupabaseS3(
  bucket: keyof typeof BUCKET_NAMES,
  key: string
): Promise<void> {
  try {
    // Validate user authentication
    await validateUserAuth();
    
    const bucketName = BUCKET_NAMES[bucket];

    const { error } = await supabase.storage
      .from(bucketName)
      .remove([key]);

    if (error) {
      
      throw error;
    }

    
  } catch (error) {
    
    throw error;
  }
}

/**
 * Helper to extract the key from a Supabase Storage URL
 */
export function extractKeyFromUrl(url: string): string | null {
  try {
    const urlParts = url.split('/');
    const bucketIndex = urlParts.findIndex(part => part === 'public' || part === 'private');
    if (bucketIndex >= 0 && urlParts.length > bucketIndex + 2) {
      // Skip 'public' and bucket name, return the rest as key
      return urlParts.slice(bucketIndex + 2).join('/');
    }
    return null;
  } catch {
    return null;
  }
}

export { BUCKET_NAMES }; 